import type { SceneObject } from 'trace-viewer/scene';
import { materializeSpatialBuild as materializeBuild } from 'trace-viewer/build';
import type { MaterializeOptions as BuildOptions, SpatialBuildContext, SpatialObjectDescriptor } from 'trace-viewer/build';
import { resolveUniqueByName, type ToolContext } from './agent-helpers';
import { catalogIndex, type IndexedAsset } from './CatalogIndex';
import { placementMetadataBehaviors, resolveAssetPlacement } from '../catalog-api';
import { executeMeshCode } from './mesh-sandbox';
import { addBakedMeshObject } from './baked-asset';
import { trackImport } from './track-import';

/** Names belong to the tool transport; engine authoring receives object references. */
export interface MaterializeOptions extends Omit<BuildOptions, 'appendTo'> { appendTo?: string; }

/** Browser catalog/persistence adapters around the engine's shared materializer. */
export async function materializeSpatialBuild(
    descriptors: SpatialObjectDescriptor[], description: string, ctx: ToolContext, options: MaterializeOptions = {},
): Promise<string> {
    const assets = new Map<string, IndexedAsset>();
    let catalogReady: Promise<void> | undefined;
    const host: SpatialBuildContext = {
        traceScene: ctx.traceScene,
        onSceneChanged: ctx.onSceneChanged,
        get pendingImages() { return ctx.pendingImages; },
        set pendingImages(value) { ctx.pendingImages = value; },
        get sceneEnvironment() { return ctx.sceneEnvironment; },
        set sceneEnvironment(value) { ctx.sceneEnvironment = value; },
        resolveAsset: async id => {
            catalogReady ??= catalogIndex.ensureLoaded();
            await catalogReady;
            const asset = catalogIndex.getById(id) ?? catalogIndex.getByUrlSubstring(id);
            if (!asset) return null;
            assets.set(asset.id, asset);
            return asset.id;
        },
        importAsset: async (id, name, track) => {
            if (!ctx.viewer?.import) throw new Error('No trace-viewer element available for asset import.');
            const asset = assets.get(id)!;
            const placement = resolveAssetPlacement({ assetId: asset.id, url: asset.url })!;
            const result = await trackImport(ctx, track, () => ctx.viewer!.import(
                placement.url, name, placementMetadataBehaviors(placement, asset.behaviors, false, ctx.traceScene)), loaded => loaded.sceneObject);
            return result.sceneObject;
        },
        executor: { mesh: executeMeshCode },
        addBakedMeshObject: (glb, name, assetName, parent, track) =>
            addBakedMeshObject(ctx, glb, name, assetName, parent, track),
        cloneBakedMeshObject: async (source, name, parent, track) => {
            const object = await trackImport(ctx, track, () => {
                if (!ctx.viewer?.import) return ctx.traceScene.createSceneObject(name, parent, source.src ?? undefined);
                const data = source.getSceneObjectData();
                return ctx.traceScene.createSceneObjectFromData({ ...data, name, transform: { ...data.transform, parent: parent.name } }, parent);
            }, loaded => loaded);
            return object;
        },
    };
    if (descriptors.some(object => object.template === 'asset') && !ctx.viewer?.import) {
        throw new Error('No trace-viewer element available for asset import.');
    }
    const appendTo: SceneObject | undefined = options.appendTo
        ? resolveUniqueByName(ctx.traceScene, options.appendTo, 'parent') : undefined;
    return materializeBuild(descriptors, description, host, { ...options, appendTo });
}
